/**
 * Render IRGraph as a draw.io Class Diagram.
 * Uses draw.io's swimlane-based UML class shape with compartments.
 */

import type { LayoutResult, IRNode, ColorPalette } from '../types';
import { NodeShape } from '../types';
import { getShapeColors, applyColors } from '../color';
import { IdGen, esc, wrapMxfile, edgeCell, labelCell } from './xml-builder';
import type { ClassMember, ClassMethod } from '../parsers/mermaid-class';

const CLASS_HEADER_STYLE =
  'swimlane;fontStyle=1;align=center;startSize=26;html=1;childLayout=stackLayout;horizontal=1;horizontalStack=0;resizeParent=1;resizeParentMax=0;collapsible=1;marginBottom=0;';

const SEPARATOR_STYLE =
  'line;strokeWidth=1;fillColor=none;align=left;verticalAlign=middle;spacingTop=-1;spacingLeft=3;spacingRight=10;rotatable=0;labelPosition=left;points=[];portConstraint=eastwest;strokeColor=inherit;html=1;';

const MEMBER_STYLE =
  'text;strokeColor=none;fillColor=none;align=left;verticalAlign=top;spacingLeft=4;spacingRight=4;overflow=hidden;rotatable=0;points=[];portConstraint=eastwest;fontStyle=0;html=1;';

export function renderDrawioClass(
  layout: LayoutResult,
  palette: ColorPalette,
): string {
  const gen = new IdGen();
  const graph = layout.graph;
  const cells: string[] = [];

  const nodeCellMap = new Map<string, string>();

  for (const [nid, node] of graph.nodes) {
    if (node.x === undefined || node.y === undefined) continue;
    if (node.shape !== NodeShape.ClassBox) continue;

    const members = (node.meta?.members ?? []) as ClassMember[];
    const methods = (node.meta?.methods ?? []) as ClassMethod[];
    const annotations = (node.meta?.annotations ?? []) as string[];

    // Class header cell (swimlane container)
    const classId = gen.next();
    nodeCellMap.set(nid, classId);

    let headerStyle = CLASS_HEADER_STYLE;
    const colors = getShapeColors(node.shape, palette);
    headerStyle = applyColors(headerStyle, colors);

    const headerLabel = annotations.length > 0
      ? `&lt;&lt;${esc(annotations[0])}&gt;&gt;\\n${esc(node.label)}`
      : esc(node.label);

    const x = layout.containerX + (node.x ?? 0);
    const y = layout.containerY + (node.y ?? 0);

    cells.push(
      `        <mxCell id="${classId}" value="${headerLabel}" ` +
      `style="${headerStyle}" vertex="1" parent="1">\n` +
      `          <mxGeometry x="${x}" y="${y}" ` +
      `width="${node.width}" height="${node.height}" as="geometry">\n` +
      `            <mxRectangle width="${node.width}" height="26" as="alternateBounds" />\n` +
      `          </mxGeometry>\n` +
      `        </mxCell>`,
    );

    // Attributes compartment
    let attrY = 26;
    for (const m of members) {
      const memberId = gen.next();
      const label = `${m.visibility}${m.name}: ${m.type}`;
      cells.push(
        `        <mxCell id="${memberId}" value="${esc(label)}" ` +
        `style="${MEMBER_STYLE}" vertex="1" parent="${classId}">\n` +
        `          <mxGeometry y="${attrY}" width="${node.width}" height="20" as="geometry" />\n` +
        `        </mxCell>`,
      );
      attrY += 20;
    }

    // Separator between attributes and methods
    if (members.length > 0 || methods.length > 0) {
      const sepId = gen.next();
      cells.push(
        `        <mxCell id="${sepId}" value="" ` +
        `style="${SEPARATOR_STYLE}" vertex="1" parent="${classId}">\n` +
        `          <mxGeometry y="${attrY}" width="${node.width}" height="8" as="geometry" />\n` +
        `        </mxCell>`,
      );
      attrY += 8;
    }

    // Methods compartment
    for (const m of methods) {
      const methodId = gen.next();
      const label = `${m.visibility}${m.name}(): ${m.returnType}`;
      cells.push(
        `        <mxCell id="${methodId}" value="${esc(label)}" ` +
        `style="${MEMBER_STYLE}" vertex="1" parent="${classId}">\n` +
        `          <mxGeometry y="${attrY}" width="${node.width}" height="20" as="geometry" />\n` +
        `        </mxCell>`,
      );
      attrY += 20;
    }
  }

  // Render relationship edges
  for (const edge of graph.edges) {
    const srcCell = nodeCellMap.get(edge.source);
    const tgtCell = nodeCellMap.get(edge.target);
    if (!srcCell || !tgtCell) continue;

    const extras = (edge.meta?.edgeStyleExtras as string) ?? '';
    const style = `edgeStyle=orthogonalEdgeStyle;rounded=0;orthogonalLoop=1;jettySize=auto;html=1;${extras}`;

    const edgeId = gen.next();
    const routePoints = edge.meta?.routePoints as Array<{ x: number; y: number }> | undefined;
    cells.push(edgeCell(edgeId, '', style, '1', srcCell, tgtCell, routePoints));

    if (edge.label) {
      const lblId = gen.next();
      cells.push(labelCell(lblId, edge.label, edgeId));
    }
  }

  return wrapMxfile(cells);
}
